import { BookingRequirement, RequirementType } from "../types/VenueGroup";

export type RequirementValue = string | number | boolean | string[] | null;

/**
 * Formats a requirement value for display based on its field type.
 */
export const formatRequirementValue = (
  type: RequirementType,
  value: RequirementValue | undefined,
  fieldConfig?: BookingRequirement
): string => {
  if (value === null || value === undefined) {
    return "";
  }

  switch (type) {
    case "boolean":
      return value === true || value === "true" ? "Yes" : "No";

    case "multiselect": {
      if (Array.isArray(value)) {
        return resolveOptionLabels(value, fieldConfig).join(", ");
      }
      return String(value);
    }

    case "select": {
      const stringValue = String(value);
      return resolveOptionLabel(stringValue, fieldConfig);
    }

    case "text":
    case "longtext":
    default:
      if (Array.isArray(value)) {
        return value.join(", ");
      }
      return String(value);
  }
};

/**
 * Resolves a single option value to its label if options are configured.
 * Falls back to the raw value if no matching option is found.
 */
const resolveOptionLabel = (
  value: string,
  fieldConfig?: BookingRequirement
): string => {
  if (!fieldConfig?.options?.length) {
    return value;
  }
  const match = fieldConfig.options.find(
    (option) => option.toLowerCase() === value.toLowerCase()
  );
  return match ?? value;
};

/**
 * Resolves multiple option values to their labels.
 */
const resolveOptionLabels = (
  values: string[],
  fieldConfig?: BookingRequirement
): string[] => {
  return values.map((v) => resolveOptionLabel(v, fieldConfig));
};

/**
 * Detects which options in a multiselect value have been deleted from
 * the current configuration options list.
 */
export const getDeletedOptions = (
  selectedValues: string[],
  availableOptions: string[]
): string[] => {
  if (!selectedValues || !Array.isArray(selectedValues)) {
    return [];
  }

  if (!availableOptions || !Array.isArray(availableOptions)) {
    return selectedValues;
  }

  return selectedValues.filter(
    (value) =>
      !availableOptions.some((opt) => opt.toLowerCase() === value.toLowerCase())
  );
};

/**
 * Determines whether a multiselect option is a deleted option
 * (i.e. was previously selected but no longer exists in venue config).
 */
export const isDeletedOption = (
  value: string,
  availableOptions: string[]
): boolean => {
  return !availableOptions.some(
    (opt) => opt.toLowerCase() === value.toLowerCase()
  );
};
